import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Checkbox,
  CheckboxGroup,
  VStack,
  Textarea,
  useToast,
  Spinner,
  Flex,
  useColorModeValue,
  Grid,
  GridItem,
  Text,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  HStack,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { useAuth } from "../context/AuthProvider";
import { useQuota } from "../context/QuotaContext";
import { addDoc, collection, Timestamp, updateDoc, serverTimestamp, doc } from "firebase/firestore";
import { firestore } from "../firebase/firebaseConfig";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getFlagByCountryCode } from "../utils/useFlags"; // Import flag utility
import countryCodes from "../components/data/CountryCodes.json"; // Import country codes
import paymentMethodsData from "../components/data/paymentMethods.json"; // ✅ Import JSON file
import PaymentMethodsDropdown from "./PaymentMethodsDropdown";

// Allowed countries (based on country names or codes)
const allowedCountries = ["United States", "Canada", "Cameroon"];

// Allowed currencies (by currency codes)
const allowedCurrencies = ["USD", "CAD", "XAF"];

const PostListingForm = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { kycVerified, canProceed, usedFreeTrade, loading, freeTrades, tradesThisCycle, quotaExceeded } = useQuota();
  const toast = useToast();
  const navigate = useNavigate();

  const textColor = useColorModeValue("brand.400", "white");
  const bgColor = useColorModeValue("brand.100", "gray.900");

  const defaultOfferedCountry = countryCodes.find((c) => c.name === "Canada") || {};
  const defaultDesiredCountry = countryCodes.find((c) => c.name === "Cameroon") || {};

  const [formData, setFormData] = useState({
    offeredCurrency: defaultOfferedCountry.currency || "CAD",
    desiredCurrency: defaultDesiredCountry.currency || "XAF",
    offeredCountry: defaultOfferedCountry.name || "Canada",
    desiredCountry: defaultDesiredCountry.name || "Cameroon",
    offeredAmount: "",
    desiredAmount: "", // Computed based on offeredAmount and rate
    town: "",
    rate: "",
    paymentMethods: [],
    location: "",
    additionalInfo: "",
  });


  const [isSubmitting, setIsSubmitting] = useState(false);
  //const currencies = ["USD", "CAD", "XAF"];

  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  const [selectedCountry, setSelectedCountry] = useState(defaultDesiredCountry);
  // ✅ Ensure selectedCurrency has full data
  const [selectedOfferedCurrency, setSelectedOfferedCurrency] = useState({
    code: defaultOfferedCountry.currency || "CAD",
    name: allowedCurrencies.find((c) => c === defaultOfferedCountry.currency) ? defaultOfferedCountry.currency : "CAD",
    flag: getFlagByCountryCode(defaultOfferedCountry.code),
  });

  // ✅ Ensure selectedCurrency has full data
  const [selectedDesiredCurrency, setSelectedDesiredCurrency] = useState({
    code: defaultDesiredCountry.currency || "XAF",
    name: allowedCurrencies.find((c) => c === defaultDesiredCountry.currency) ? defaultDesiredCountry.currency : "XAF",
    flag: getFlagByCountryCode(defaultDesiredCountry.code),
  });

  const [uniqueCurrencies, setUniqueCurrencies] = useState([]);
  //const [amount, setAmount] = useState("");
  const [paymentMethods, setPaymentMethods] = useState([]);

  const availableOfferedCurrencies = uniqueCurrencies.filter(
    (currency) => currency.code !== formData.desiredCurrency
  );

  const availableDesiredCurrencies = uniqueCurrencies.filter(
    (currency) => currency.code !== formData.offeredCurrency
  );

  // ✅ Load payment methods from JSON
  useEffect(() => {
    setPaymentMethods(paymentMethodsData.paymentMethods);
  }, []);

  useEffect(() => {
    if (!formData.offeredCountry || !formData.offeredCurrency) {
      setFormData((prev) => ({
        ...prev,
        offeredCountry: defaultOfferedCountry.name,
        offeredCurrency: defaultOfferedCountry.currency,
      }));
    }

    if (!formData.desiredCountry || !formData.desiredCurrency) {
      setFormData((prev) => ({
        ...prev,
        desiredCountry: defaultDesiredCountry.name,
        desiredCurrency: defaultDesiredCountry.currency,
      }));
    }
  }, []);



  // Initialize country and currency defaults
  useEffect(() => {

    // Filter countries based on the allowedCountries array
    const filteredCountries = countryCodes.filter((country) =>
      allowedCountries.includes(country.name)
    );


    // Generate and set filtered currency list
    const currencyMap = {};
    filteredCountries.forEach((country) => {
      if (
        country.currency &&
        allowedCurrencies.includes(country.currency) &&
        !currencyMap[country.currency]
      ) {
        currencyMap[country.currency] = {
          code: country.currency,
          countryCode: country.code,
        };
      }
    });

    const currencyList = Object.values(currencyMap);
    setUniqueCurrencies(currencyList);

  }, []);

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value.trim(),
      // ✅ Auto-calculate desired amount if rate & offeredAmount are set
      desiredAmount:
        name === "offeredAmount" || name === "rate"
          ? name === "rate"
            ? prev.offeredAmount && value ? (prev.offeredAmount * value).toFixed(2) : ""
            : value && prev.rate ? (value * prev.rate).toFixed(2) : ""
          : prev.desiredAmount,
    }));
  };

  const handleLocationChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      location: {
        ...prev.location,
        [name]: value.trim(),
      },
    }));
  };


  const validateInputs = () => {
    const {
      offeredCountry,
      desiredCountry,
      offeredCurrency,
      desiredCurrency,
      offeredAmount,
      desiredAmount,
      rate,
      paymentMethods,
      location // ✅ Now expecting an object with town, state, postalCode, etc.
    } = formData;

    console.log("🔍 Validating Inputs...");
    console.log("📌 Offered Country:", offeredCountry);
    console.log("📌 Desired Country:", desiredCountry);
    console.log("💱 Offered Currency:", offeredCurrency);
    console.log("💱 Desired Currency:", desiredCurrency);
    console.log("💰 Offered Amount:", offeredAmount);
    console.log("💰 Desired Amount:", desiredAmount);
    console.log("📊 Rate:", rate);
    console.log("💳 Payment Methods:", paymentMethods);
    console.log("📍 Location:", location);

    if (!offeredCurrency || !desiredCurrency || offeredCurrency === desiredCurrency) {
      console.warn("❌ Currency selection is invalid.");
      toast({
        title: t("postlistingform.invalid_currency_title"),
        description: t("postlistingform.invalid_currency_message"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!offeredCountry || !desiredCountry) {
      console.warn("❌ Country selection is missing.");
      toast({
        title: "Country Selection Required",
        description: "Each currency must be associated with a country.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (parseFloat(offeredAmount) <= 0 || isNaN(offeredAmount)) {
      console.warn("❌ Invalid offeredAmount:", offeredAmount);
      toast({
        title: "Invalid Offered Amount",
        description: "Offered Amount must be greater than zero.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (parseFloat(rate) <= 0 || isNaN(rate)) {
      console.warn("❌ Invalid rate:", rate);
      toast({
        title: t("postlistingform.invalid_rate_title"),
        description: t("postlistingform.invalid_rate_message"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!paymentMethods || paymentMethods.length === 0) {
      console.warn("❌ No payment methods selected.");
      toast({
        title: t("postlistingform.payment_method_required_title"),
        description: t("postlistingform.payment_method_required_message"),
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    // ✅ Validate location (object)
    if (
      !location ||
      !location.town ||
      location.town.trim() === ""
    ) {
      console.warn("❌ Location details are missing.");
      toast({
        title: t("postlistingform.location_required_title"),
        description: "Town, state, and postal code are required.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    console.log("✅ All inputs are valid!");
    return true;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      toast({
        title: t("postlistingform.auth_required_title"),
        description: t("postlistingform.auth_required_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!validateInputs()) return;

    setIsSubmitting(true);

    try {

      if (loading) {
        return toast({
          title: "Checking KYC & Quota...",
          description: "Please wait while we verify your account.",
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }

      if (!kycVerified) {
        return toast({
          title: "KYC Verification Required",
          description: "You must complete KYC verification before posting a listing.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }

      if (!canProceed) {
        return toast({
          title: "Quota Limit Reached",
          description: "You have reached your transaction limit.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } else {
        console.log("tradesThisCycle:", tradesThisCycle);
        await updateDoc(doc(firestore, "users", user.uid), {
          tradesThisCycle: tradesThisCycle - 1,
        });
      }

      if (usedFreeTrade) {
        console.log("freeTrades:", freeTrades);
        await updateDoc(doc(firestore, "users", user.uid), {
          freeTrades: freeTrades - 1,
        });

        toast({
          title: "Free Trade Used!",
          description: "Your free trade has been applied. Remaining free trades updated.",
          status: "info",
          duration: 4000,
          isClosable: true,
        });
      }


      console.log("kycVerified:", kycVerified);
      console.log("loading:", loading);
      console.log("canProceed:", canProceed);
      console.log("usedFreeTrade:", usedFreeTrade);


      const exchangeRatePair = `${formData.desiredCurrency}/${formData.offeredCurrency}`;
      const clientTimestamp = Timestamp.fromMillis(Date.now());

      const listingRef = await addDoc(collection(firestore, "listings"), {
        ...formData,
        exchangeRatePair,
        userId: user.uid,
        status: "active",
        createdAt: serverTimestamp(),
        createdAtFallback: clientTimestamp,
      });

      const expiresAt = Timestamp.fromMillis(clientTimestamp.toMillis() + 24 * 60 * 60 * 1000);
      await updateDoc(listingRef, { expiresAt });

      toast({
        title: t("postlistingform.listing_posted_title"),
        description: t("postlistingform.listing_posted_message", { pair: exchangeRatePair }),
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/personal/listings");
    } catch (error) {
      console.error(t("postlistingform.posting_error"), error);
      toast({
        title: t("postlistingform.posting_error_title"),
        description: t("postlistingform.posting_error_message"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  const handleOfferedCurrencySelect = (currency) => {
    console.log("💱 Offered Currency Selected:", currency.code);

    // Filter allowed countries first
    const filteredCountries = countryCodes.filter((c) =>
      allowedCountries.includes(c.name)
    );

    const country = filteredCountries.find((c) => c.currency === currency.code);

    if (!country) {
      console.warn(`⚠️ No allowed country found for currency: ${currency.code}`);
    }

    setFormData((prev) => ({
      ...prev,
      offeredCurrency: currency.code,
      offeredCountry: country ? country.name : prev.offeredCountry,
    }));

    setSelectedOfferedCurrency({
      code: currency.code,
      name: currency.name || country?.currency,
      flag: getFlagByCountryCode(country?.code),
    });
  };

  const handleDesiredCurrencySelect = (currency) => {
    console.log("💱 Desired Currency Selected:", currency.code);

    const filteredCountries = countryCodes.filter((c) =>
      allowedCountries.includes(c.name)
    );

    const country = filteredCountries.find((c) => c.currency === currency.code);

    if (!country) {
      console.warn(`⚠️ No allowed country found for currency: ${currency.code}`);
    }

    setFormData((prev) => ({
      ...prev,
      desiredCurrency: currency.code,
      desiredCountry: country ? country.name : prev.desiredCountry,
    }));

    setSelectedDesiredCurrency({
      code: currency.code,
      name: currency.name || country?.currency,
      flag: getFlagByCountryCode(country?.code),
    });
  };






  return (
    <Box bg={bgColor} p={6} borderRadius="lg" shadow="md" > {/* Removed Card, added padding to Box */}
      <form onSubmit={handleSubmit}>
        <VStack spacing={8} align="stretch"> {/* Increased spacing */}


          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} alignItems="center"> {/* Grid for Currencies */}


            <GridItem>{/* Offered Amount & Currency */}
              <FormControl id="offeredAmount" isRequired>
                <FormLabel fontSize="xl" fontWeight={"bold"} color={useColorModeValue("brand.400", "white")}>
                  I Have
                </FormLabel>
                <Grid templateColumns="repeat(5, 1fr)" gap={2}>
                  {/* Offered mount Input */}
                  <GridItem colSpan={3}>

                    <Input
                      type="number"
                      name="offeredAmount"
                      min="1"
                      bg="white"
                      fontSize="lg"
                      fontWeight="bold"
                      value={formData.offeredAmount}
                      onChange={handleChange}
                      placeholder="Enter amount"
                      color={textColor}
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      borderRadius="md"
                      px={4}
                      py={3}
                      boxShadow="sm"
                      size={"xl"}
                      borderWidth="1px"
                      borderColor={useColorModeValue("gray.300", "gray.600")}
                      w="full"
                      h="54px"
                      rounded="md"
                      _hover={{ bg: useColorModeValue("gray.100", "gray.600") }}
                      _active={{ bg: useColorModeValue("gray.200", "gray.500") }}
                    />
                  </GridItem>

                  {/* Offered Currency Select */}
                  <GridItem colSpan={2}>

                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        bg="white"
                        color="brand.500"
                        fontSize="md"
                        fontWeight="bold"
                        w="full"
                        size="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        rounded="md"
                        h="52px"
                        _hover={{ bg: useColorModeValue("gray.100", "gray.600") }}
                        _active={{ bg: useColorModeValue("gray.200", "gray.500") }}
                      >
                        {selectedOfferedCurrency.flag ? (
                          <HStack spacing={2}>
                            <Image
                              src={selectedOfferedCurrency.flag}
                              alt={`${selectedOfferedCurrency.code} flag`}
                              boxSize="20px"
                              borderRadius="full"
                            />
                            <Text fontWeight="bold">{selectedOfferedCurrency.code}</Text>
                          </HStack>
                        ) : (
                          <Text fontWeight="bold">`${selectedOfferedCurrency.code} - ${selectedOfferedCurrency.name}`</Text>
                        )}
                      </MenuButton>
                      <MenuList>
                        {availableOfferedCurrencies.map((currency) => (
                          <MenuItem
                            name="offeredCurrency"
                            key={currency.code}
                            value={currency.code}
                            onClick={() => handleOfferedCurrencySelect(currency)}
                          >
                            <Image
                              src={getFlagByCountryCode(currency.countryCode)}
                              alt={`${currency.code} flag`}
                              boxSize="20px"
                              mr={2}
                            />
                            <Text fontWeight="bold">{currency.code}</Text>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </GridItem>

                </Grid>
              </FormControl>
            </GridItem>

            <GridItem>
              {/* Desired Amount & Currency */}
              <FormControl id="desiredAmount" isRequired>
                <FormLabel
                  fontSize="lg"
                  fontWeight="semibold"
                  color={useColorModeValue("gray.700", "gray.200")}
                  mb={2}
                >
                  I Want
                </FormLabel>

                <Grid templateColumns="repeat(5, 1fr)" gap={2} alignItems="center">
                  <GridItem colSpan={3}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-end"
                      bg={useColorModeValue("gray.50", "gray.700")}
                      borderRadius="md"
                      px={4}
                      py={3}
                      boxShadow="sm"
                      width="full"
                    >
                      <Text
                        fontSize="xl"
                        fontWeight="bold"
                        color={useColorModeValue("gray.900", "white")}
                      >
                        {
                          !isNaN(parseFloat(formData.desiredAmount)) && formData.desiredAmount !== ""
                            ? parseFloat(formData.desiredAmount).toLocaleString()
                            : "0"
                        }
                      </Text>
                    </Box>
                  </GridItem>

                  <GridItem colSpan={2}>
                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        bg={useColorModeValue("white", "gray.700")}
                        color={useColorModeValue("gray.800", "white")}
                        borderWidth="1px"
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        fontSize="md"
                        fontWeight="bold"
                        w="full"
                        h="54px"
                        width="full"
                        rounded="md"
                        _hover={{ bg: useColorModeValue("gray.100", "gray.600") }}
                        _active={{ bg: useColorModeValue("gray.200", "gray.500") }}
                      >
                        {selectedDesiredCurrency.flag ? (
                          <HStack spacing={2}>
                            <Image
                              src={selectedDesiredCurrency.flag}
                              alt={`${selectedDesiredCurrency.code} flag`}
                              boxSize="20px"
                              borderRadius="full"
                            />
                            <Text fontWeight="bold">{selectedDesiredCurrency.code}</Text>
                          </HStack>
                        ) : (
                          <Text fontWeight="bold">`${selectedDesiredCurrency.code} - ${selectedDesiredCurrency.name}`</Text>
                        )}
                      </MenuButton>

                      <MenuList zIndex={5}>
                        {availableDesiredCurrencies.map((currency) => (
                          <MenuItem
                            name="desiredCurrency"
                            key={currency.code}
                            value={currency.code}
                            onClick={() => handleDesiredCurrencySelect(currency)}
                          >
                            <HStack spacing={3}>
                              <Image
                                src={getFlagByCountryCode(currency.countryCode)}
                                alt={`${currency.code} flag`}
                                boxSize="20px"
                              />
                              <Text fontWeight="bold">{currency.code}</Text>
                            </HStack>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </GridItem>
                </Grid>
              </FormControl>
            </GridItem>

          </Grid> {/* End of Grid for Currencies */}

          {/* Rate & Town Selection */}
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} alignItems="center"> {/* Grid for Amount and Rate */}
            <GridItem>
              <FormControl id="rate" isRequired>
                <FormLabel fontSize="xl" fontWeight={"bold"} color={textColor}>
                  At
                </FormLabel>
                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                  <GridItem colSpan={1}>
                    <Input
                      type="number"
                      name="rate"
                      min="1"
                      fontSize="lg"
                      fontWeight="bold"
                      value={formData.rate}
                      onChange={handleChange}
                      placeholder="Rate"
                      textAlign="right" // Align input text to the right
                      bg={useColorModeValue("white", "gray.700")}
                      color={textColor}
                      borderWidth="1px"
                      borderColor={useColorModeValue("gray.300", "gray.600")}
                      w="full"
                      h="54px"
                      rounded="md"
                      _hover={{ bg: useColorModeValue("gray.100", "gray.600") }}
                      _active={{ bg: useColorModeValue("gray.200", "gray.500") }}
                    />
                  </GridItem>
                  <GridItem colSpan={1} display="flex" alignItems="center" justifyContent="flex-start">

                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      bg={useColorModeValue("gray.50", "gray.700")}
                      borderRadius="md"
                      px={4}
                      py={3}
                      boxShadow="sm"
                      w="full"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="lg" // Reduced font size for better visual hierarchy
                        color={useColorModeValue("gray.600", "gray.400")} // Use a subtle color
                      >
                        ({formData.desiredCurrency || t("postlistingform.desired_currency_placeholder")} /{" "}
                        {formData.offeredCurrency || t("postlistingform.offered_currency_placeholder")})
                      </Text>
                    </Box>

                  </GridItem>
                </Grid>
              </FormControl>
            </GridItem>

            <GridItem>

              <FormControl id="town" isRequired>
                <FormLabel fontSize="xl" fontWeight={"bold"} color={textColor}>
                  In
                </FormLabel>
                <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                  <GridItem colSpan={1}>
                    <Input
                      type="text"
                      name="town"
                      min="1"
                      bg="white"
                      fontSize="lg"
                      fontWeight="bold"
                      value={formData.location?.town}
                      onChange={handleLocationChange}
                      placeholder="Town"
                      color={textColor}
                      borderWidth="1px"
                      borderColor={useColorModeValue("gray.300", "gray.600")}
                      w="full"
                      h="54px"
                      rounded="md"
                      _hover={{ bg: useColorModeValue("gray.100", "gray.600") }}
                      _active={{ bg: useColorModeValue("gray.200", "gray.500") }}
                    />
                  </GridItem>
                  <GridItem colSpan={1} display="flex" alignItems="center" justifyContent="flex-start">
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="flex-start"
                      bg={useColorModeValue("gray.50", "gray.700")}
                      borderRadius="md"
                      px={4}
                      py={3}
                      boxShadow="sm"
                      w="full"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="xl"
                        color={useColorModeValue("gray.600", "gray.400")}
                      >
                        {formData.desiredCountry}
                      </Text>
                    </Box>
                  </GridItem>
                </Grid>

              </FormControl>
            </GridItem>
          </Grid> {/* End of Grid for Amount and Rate */}

          <FormControl id="paymentMethods">
            <FormLabel fontSize="xl" fontWeight={"bold"} color={textColor}>
              {t("postlistingform.payment_methods_label")}
            </FormLabel>


            <PaymentMethodsDropdown
              selectedMethods={formData.paymentMethods}
              onChange={(selected) => setFormData((prev) => ({ ...prev, paymentMethods: selected }))}
              onRemove={(id) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentMethods: prev.paymentMethods.filter((method) => method !== id),
                }))
              }
            />

          </FormControl>
          <FormControl id="additionalInfo">
            <FormLabel fontSize="xl" fontWeight={"bold"} color={textColor}>
              {t("postlistingform.additional_info_label")}
            </FormLabel>
            <Textarea
              name="additionalInfo"
              value={formData.additionalInfo}
              onChange={handleChange}
              bg="white"
              fontSize="lg"
              fontWeight="bold"
              placeholder={t("postlistingform.additional_info_placeholder")}
              color={textColor}
            />
          </FormControl>

          <Flex justify="center" mt={6}> {/* Increased margin top */}
            <Button
              bg="brand.300"
              color="white"
              type="submit"
              colorScheme="brand.300"
              isDisabled={!kycVerified || loading || (!canProceed && !usedFreeTrade) || isSubmitting || quotaExceeded} // ✅ Now disables button if quotaExceeded is true
              w="full">
              {isSubmitting ? <Spinner size="sm" /> : t("postlistingform.post_listing_button")}
            </Button>

          </Flex>
          {quotaExceeded && (
            <Text color="red.500" fontSize="md" mt={2}>
              {t("postlistingform.quota_exceeded_message")}
            </Text>
          )}
        </VStack>
      </form>
    </Box>
  );
};

export default PostListingForm;